import { Builder, BuilderComponent } from '@builder.io/react';
import hoursToSeconds from 'date-fns/hoursToSeconds';
import type { GetStaticProps, NextPage } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { InternalLocale } from '@catawiki/locale';

import { LocalAssets, fetchAssets } from '@/shared/api/assets';
import { Link } from '@/shared/components/Link';
import { INVALIDATE_EMPTY_ASSETS_SECONDS } from '@/shared/constants/next';

interface Props {
  locale: string;
  assetsData: LocalAssets;
}

const Page: NextPage<Props> = ({ locale }) => {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const title = `${t('seo.not_found')} - Catawiki`;
  // isEditing and isPreview are true only when this page
  // open in Builder.io or has Builder.io parameters.
  // Otherwise both will alway be false
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    setIsEditing(Builder.isEditing || Builder.isPreviewing);
  }, []);

  if (isEditing) {
    return (
      <BuilderComponent
        model={asPath.startsWith('/pages/qa') ? 'qa' : 'page'}
        data={{ locale }}
        context={{ locale }}
        renderLink={(props) => <Link {...props} />}
      />
    );
  }

  return (
    <>
      <Head>
        <title>{title}</title>
      </Head>
      <div className="u-wrapper u-m-v-lg-xxl-18 u-m-v-xxl-6">
        <h3 className="u-text-center u-typography-h3">{t('seo.not_found')}</h3>
      </div>
    </>
  );
};

export const revalidateTime = (assetsData: LocalAssets) => {
  // We want to invalidate 404 pages on non-production more faster for other services QA
  // https://catawiki.atlassian.net/browse/CNC-1307
  if (process.env.APP_ENV !== 'production') {
    return INVALIDATE_EMPTY_ASSETS_SECONDS;
  }
  if (assetsData.error) return INVALIDATE_EMPTY_ASSETS_SECONDS;
  return hoursToSeconds(72);
};

export const getStaticProps: GetStaticProps = async ({ locale = 'en' }) => {
  const [translations, assetsData] = await Promise.all([
    serverSideTranslations(locale),
    fetchAssets(locale as InternalLocale),
  ]);

  const revalidate = revalidateTime(assetsData);

  return {
    props: {
      // Log revalidate time
      revalidateTime: revalidate,
      // assetData will be passed to Layout component in _app
      assetsData,
      locale,
      ...translations,
    },
    // Next.js will attempt to re-generate the page:
    revalidate,
  };
};

export default Page;
